<script>
import moment from 'moment'
import BaseResource from '@/resources'
import TrackResource from '@/resources/track'
import UserResource from '@/resources/user'
import CarResource, { CarSkinResource } from '@/resources/car'
import { RaceEventResource, RaceEntryResource, RaceSessionResource } from '@/resources/launcher.js'
import EventForm from './EventForm.vue'
import PartyForm from './PartyForm.vue'
import PartyName from './PartyName.vue'

export default {
    components: {
        PartyForm,
        EventForm,
        PartyName
    },
    props: {
        party: {
            type: BaseResource,
            required: true
        }
    },
    computed: {
        resources() {
            return {
                UserResource,
                TrackResource,
                CarResource,
                CarSkinResource
            }
        },
        eventId() {
            return this.party.attributes.host_event
        },
        eventEditSessionDurations() {
            let minutes = 0
            let laps = 0

            for (let session of this.eventEditSessions) {
                if (session.duration_is_laps) {
                    laps += session.duration
                } else {
                    minutes += Math.round(session.duration + 1.2)
                }
            }

            const now = moment()
            const end = now.add(minutes, 'minutes').format('h:mm a')
            
            return { minutes, laps, end }
        },
        eventButtonLabel() {
            if (this.eventHasChanges) {
                return 'Save All'
            }

            return 'Start All'
        },
        eventButtonAction() {
            if (this.eventHasChanges) {
                return this.eventSave
            }

            return this.$refs.eventComponent.startOnHost
        },
    },
    watch: {},
    data() {
        return {
            event: null,
            eventEdit: null,
            eventEditSessionPractice: true,
            eventEditSessionPracticeDuration: 25,
            eventEditSessionQualifying: true,
            eventEditSessionQualifyingDuration: 10,
            eventEditSessionRace: true,
            eventEditSessionRaceDuration: 10,
            eventEditSessionRaceLaps: false,
            eventHasChanges: false,
            socketConnected: null,
            serverRunning: null
        }
    },
    methods: {
        async load() {
            if (this.party.attributes.host_event) {
                this.event = await RaceEventResource.detail(this.party.attributes.host_event)
                this.event.socketWatchForChanges()
                this.event.emitter.on('open', () => this.socketConnected = true)
                this.event.emitter.on('close', () => this.socketConnected = false)
                this.event.emitter.on('server', (isRunning) => this.serverRunning = isRunning)
            }
            
            this.loading = false
        },
        async eventSave() {
            if (this.$refs.eventComponent.validate()) {
                this.eventHasChanges = false
                await this.$refs.eventComponent.save()
            }
        },
        async save() {
            this.eventSave()
        },
        async onEventEdit() {
            await this.event.resolveRelated({ managers: ['track'] })
            this.eventEdit = this.event
            this.$nextTick(() => this.$refs.eventEditModal.show())
        },
        async onPartyDelete() {
            await this.party.delete()
            this.$emit('deleteParty')
        },
        async onPartyUpdate() {
            this.$refs.eventComponent.reloadOnAll()
        },
        randomCarOnAll(extraFilters = {}) {
            for (let member of this.$refs.eventComponent.members) {
                this.$refs.eventComponent.randomCar(member, extraFilters)
            }
        },
        randomSkinOnAll() {
            for (let member of this.$refs.eventComponent.members) {
                this.$refs.eventComponent.randomSkin(member)
            }
        }
    },
    async mounted() {
        await this.load()
    },
    beforeDestroy() {
        // if (this.event) {
        //     this.event.socketClose()
        // }
    }
}
</script>

<template>
    <b-card>
        <template #header>
            <b-row>
                <b-col class="overflow-hidden">
                    <div class="pt-1 party-name">
                        <party-name :party="party" @update="onPartyUpdate()" editable />
                        <b-icon icon="hdd" class="ml-2" v-if="serverRunning" />
                        <b-icon icon="plug-fill" :class="{ 'text-danger': !socketConnected, 'text-success': socketConnected, 'ml-2': true }" /> 
                    </div>
                </b-col>
                <b-col class="text-right">
                    <b-button-group>
                        <b-button variant="light" size="sm" v-if="$refs.eventComponent && $refs.eventComponent.userPresetsExistForHost" @click="$refs.eventComponent.presetLoadForHost()" title="Der Host hat Benutzervoreinstellungen definiert. Klicken Sie, um die neueste Voreinstellung zu laden" v-b-tooltip.hover><b-icon icon="collection-play" /></b-button>
                        <b-button variant="light" size="sm" v-if="$refs.eventComponent && $refs.eventComponent.loggedInUserPresets.length" @click="$refs.eventComponent.presetsOpenLoggedInUserModal()" title="Laden Sie eine Ihrer Voreinstellungen" v-b-tooltip.hover>
                            <b-iconstack>
                                <b-icon stacked icon="collection-play" scale="0.6" shift-h="-4" shift-v="2" />
                                <b-icon stacked icon="person-fill" scale="0.8" shift-v="-3" shift-h="5" />
                            </b-iconstack>
                        </b-button>
                        <b-button variant="light" size="sm" @click="$refs.eventComponent.acInGameGridOnAll()" title="Alle ins Gitter" v-b-tooltip.hover><b-icon icon="download" rotate="180" /></b-button>
                        <b-button variant="light" size="sm" @click="$refs.eventComponent.acInGamePitOnAll()" title="Alle in die Box schicken" v-b-tooltip.hover><b-icon icon="house" /></b-button>
                    </b-button-group>
                    <b-button-group>
                        <b-dropdown size="sm" :variant="eventButtonLabel.match('Speichern') ? 'outline-secondary' : 'light'" :text="eventButtonLabel" @click="eventButtonAction()" right split>
                            <b-dropdown-header>Party-Aktionen</b-dropdown-header>
                            <b-dropdown-item @click="$refs.eventComponent.startOnHost()" variant="success">Sitzung starten</b-dropdown-item>
                            <b-dropdown-item @click="$refs.eventComponent.endOnAll()" variant="danger">Sitzung beenden</b-dropdown-item>
                            <b-dropdown-item @click="$refs.eventComponent.reloadOnAll()">Alle neu laden</b-dropdown-item>
                            <b-dropdown-item @click="$refs.eventComponent.wakeAll()">Alle wecken</b-dropdown-item>
                            <b-dropdown-item @click="$refs.eventComponent.partyDestroy()" variant="danger">Party auflösen</b-dropdown-item>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-header>Autos</b-dropdown-header>
                            <b-dropdown-item @click="randomCarOnAll({ groups: 'GT3' })">Zufälliges <strong>GT3</strong> für alle</b-dropdown-item>
                            <b-dropdown-item @click="randomCarOnAll({ groups: 'F1' })">Zufälliges <strong>F1</strong> für alle</b-dropdown-item>
                            <b-dropdown-item @click="randomCarOnAll({ groups: 'STREET' })">Zufälliges <strong>Straßen</strong> für alle</b-dropdown-item>
                            <b-dropdown-item @click="randomCarOnAll({ id__startswith: 'ks_' })">Zufälliges <strong>Kunos</strong> für alle</b-dropdown-item>
                            <b-dropdown-item @click="randomSkinOnAll()">Zufälliger Skin für alle</b-dropdown-item>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-header>Modals</b-dropdown-header>
                            <b-dropdown-item @click="$refs.eventComponent.partyDisplayMultiplayerBrief()">Multiplayer-Brief anzeigen</b-dropdown-item>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-header>Debug</b-dropdown-header>
                            <b-dropdown-item @click="$refs.eventComponent.endOnAllForce()" variant="danger">Alle stoppen (erzwungenes Beenden)</b-dropdown-item>
                        </b-dropdown>
                    </b-button-group>
                </b-col>
            </b-row>
        </template>

        <messages />
        
        <event-form
            v-if="party && event"
            ref="eventComponent"
            :event="event"
            :party="party"
            @change="eventHasChanges = true"
            @deleteParty="$emit('deleteParty')"
            @boot="(calendarId) => $emit('boot', calendarId)"
            no-save
            tabs>
        </event-form>

        <div v-else-if="party && !event" :title="String(party)" class="text-center">
            <p class="text-muted">Mitglieder haben dieses Event verlassen.</p>
            <b-button variant="secondary" @click="onPartyDelete()">Löschen</b-button>
        </div>
    </b-card>
</template>

<style lang="scss" scoped>
.card-header {
    font-weight: bold;
}

.session-delete {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    background-color: #cccccc2b;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    color: #000;
}

.party-name {
    width: 500px;
    overflow: hidden;
}
</style>
